import {isString} from 'lodash';
import {useCallback, useEffect, useState} from 'react';

import {computeCrmSyncMappingFromCustomFieldMapping} from '../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {useAppDispatch} from '../../../../../../../../src/helpers/reduxHelpers';
import {CrmFieldModel} from '../../../../../../../../src/models/api/crmFieldModel';
import {CrmMetadataModel} from '../../../../../../../../src/models/api/crmMetadataModel';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../../../../src/models/api/crmSyncModel';
import {
  createCrmSyncAsyncAction,
  CrmSyncSearchOptions,
  disableCrmSyncAsyncAction,
  enableCrmSyncAsyncAction,
  fetchFieldsAsyncAction,
  fetchMetadataAsyncAction,
  searchListsAsyncAction,
  updateCrmSyncAsyncAction,
} from '../../../../../../../../src/store/data/crmSync/crmSyncsActions';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {AsyncHandler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../shared/helpers/src/types/asyncStatuses';
import {CrmSyncsFormModel} from './crmSyncsForm';
import {CrmSyncOptionsEnum, EXTERNAL_LINK_SYNC_ALL} from './crmSyncsOptions/crmSyncsOptions';
import {CrmSyncsPageResourceConfig, useCrmPageConfig} from './crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncSettingsFormModel} from './crmSyncsSettings/crmSyncsSettings';

/*
 * Hooks.
 *
 */

export function useCreateCrmSync(
  source: CrmSyncSourcesEnum,
  isConnected: boolean,
  isLoadingCrmSyncs: boolean,
  hasAccountCrmSync: boolean,
  hasCardCrmSync: boolean,
) {
  const {account, card} = useCrmPageConfig(source);
  const dispatch = useAppDispatch();
  const [hasErrorCreatingSync, setHasErrorCreatingSync] = useState<boolean>(false);

  const onCreateCrmSyncAsync = useCallback(
    async (shouldThrow: boolean) => {
      try {
        if (account && !hasAccountCrmSync) {
          await dispatch(
            createCrmSyncAsyncAction(source, CrmSyncIntegrationResourceTypesEnum.ACCOUNT, {}, shouldThrow),
          );
        }

        if (card && !hasCardCrmSync) {
          await dispatch(
            createCrmSyncAsyncAction(source, CrmSyncIntegrationResourceTypesEnum.CARD, {}, shouldThrow),
          );
        }
        setHasErrorCreatingSync(false);
      } catch (error) {
        // 409 conflict is an expected error if we try to create when the sync already exists
        if (error.status !== 409) {
          setHasErrorCreatingSync(true);
        }
      }
    },
    [account, card, dispatch, hasAccountCrmSync, hasCardCrmSync, source],
  );

  useEffect(() => {
    if (!isConnected || isLoadingCrmSyncs) {
      return;
    }

    onCreateCrmSyncAsync(true);
  }, [isConnected, isLoadingCrmSyncs, onCreateCrmSyncAsync]);

  return {
    hasErrorCreatingSync,
    onCreateCrmSyncAsync,
  };
}

export function useUpdateCrmSync(
  panelId: ContactsManagerPanelId,
  source: CrmSyncSourcesEnum,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  isEnabledOnServer: boolean,
  crmSyncId?: number,
  updatedSettingsFormState?: CrmSyncSettingsFormModel,
): {
  isUpdatingCrmSync: boolean;
  onUpdateCrmSync: AsyncHandler;
} {
  const [isUpdatingCrmSync, setIsUpdatingCrmSync] = useState<boolean>(false);
  const resourceConfig = useCrmPageConfig(source)[resourceType];
  const dispatch = useAppDispatch();

  const onUpdateCrmSync = async () => {
    if (!crmSyncId || !resourceConfig) {
      return;
    }

    setIsUpdatingCrmSync(true);
    const formState = updatedSettingsFormState && updatedSettingsFormState[resourceType];

    if (!formState && isEnabledOnServer) {
      // if there are no changes and we are enabled on the server we just run a sync
      await dispatch(enableCrmSyncAsyncAction(panelId, resourceType, crmSyncId));
      setIsUpdatingCrmSync(false);
      return;
    }

    if (formState && !formState.isEnabled) {
      // if we just disabled the sync
      await dispatch(disableCrmSyncAsyncAction(crmSyncId));
      setIsUpdatingCrmSync(false);
      return;
    }

    // if we have no changes and we are disabled on the server we don't need to do anything
    if (!formState) {
      setIsUpdatingCrmSync(false);
      return;
    }
    const selectedOption = formState.selectedListOption;
    const {mappedFields} = formState;
    const customFieldMapping = [
      ...resourceConfig.syncMapping.computeRequiredMapping(selectedOption),
      ...mappedFields,
    ];

    const listId = computeListId(resourceConfig, formState);
    const {shouldSkipNameMatch, filter} = formState;
    await dispatch(
      updateCrmSyncAsyncAction(
        panelId,
        resourceType,
        crmSyncId,
        computeCrmSyncMappingFromCustomFieldMapping(customFieldMapping),
        listId,
        shouldSkipNameMatch,
        filter,
      ),
    );

    setIsUpdatingCrmSync(false);
  };

  return {isUpdatingCrmSync, onUpdateCrmSync};
}

export function useMetadata(
  crmSyncId: number,
  isValidConnected: boolean,
  setHasExpiredCredentials: HandlerOf<boolean>,
): {metadata?: CrmMetadataModel; status: AsyncStatusesEnum} {
  const [metadata, setMetadata] = useState<CrmMetadataModel | undefined>();
  const [status, setStatus] = useState(AsyncStatusesEnum.LOADING);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isValidConnected) {
      setStatus(AsyncStatusesEnum.LOADED);
      return;
    }
    const fetchMetadataAsync = async () => {
      try {
        setStatus(AsyncStatusesEnum.LOADING);
        const fetchedMetadata = await dispatch(fetchMetadataAsyncAction(crmSyncId));
        setMetadata(fetchedMetadata);
        setStatus(AsyncStatusesEnum.LOADED);
      } catch (error) {
        if (checkForCredentialsError(error)) {
          setHasExpiredCredentials(true);
        }
        setStatus(AsyncStatusesEnum.FAILED);
      }
    };

    fetchMetadataAsync();
  }, [crmSyncId, isValidConnected, setMetadata, setHasExpiredCredentials, dispatch]);

  return {metadata, status};
}

export function useCrmFields(
  crmSyncId: number,
  isValidConnected: boolean,
  setHasExpiredCredentials: HandlerOf<boolean>,
): {crmFields?: ReadonlyArray<CrmFieldModel>; status: AsyncStatusesEnum} {
  const [crmFields, setAccountFields] = useState<ReadonlyArray<CrmFieldModel> | undefined>(undefined);
  const [status, setStatus] = useState(AsyncStatusesEnum.LOADING);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isValidConnected) {
      setStatus(AsyncStatusesEnum.LOADED);
      return;
    }

    const fetchFieldsAsync = async () => {
      try {
        setStatus(AsyncStatusesEnum.LOADING);
        const fields = await dispatch(fetchFieldsAsyncAction(crmSyncId));
        if (fields && fields.length > 0) {
          setAccountFields(fields);
        }
        setStatus(AsyncStatusesEnum.LOADED);
      } catch (error) {
        if (checkForCredentialsError(error)) {
          setHasExpiredCredentials(true);
        }
        setStatus(AsyncStatusesEnum.FAILED);
      }
    };

    fetchFieldsAsync();
  }, [crmSyncId, isValidConnected, setAccountFields, setHasExpiredCredentials, dispatch]);

  return {crmFields, status};
}

export function useSearchLists(
  source: CrmSyncSourcesEnum,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  isValidConnected: boolean,
) {
  const dispatch = useAppDispatch();
  const [hasNoListAccess, setHasNoListAccess] = useState<boolean>(false);
  const resourceConfig = useCrmPageConfig(source)[resourceType];

  const onSearchListsAsync = useCallback(
    async (query: string, options: CrmSyncSearchOptions) => {
      try {
        const response = await dispatch(searchListsAsyncAction(source, resourceType, query, options));
        setHasNoListAccess(false);
        return response;
      } catch (err) {
        if (checkHasListAccessError(err)) {
          setHasNoListAccess(true);
        }
      }
      return undefined;
    },
    [dispatch, resourceType, source],
  );

  // try to fetch lists initially to check if they have list access
  useEffect(() => {
    if (!resourceConfig?.syncOptions.LISTS || !isValidConnected) {
      return;
    }
    onSearchListsAsync('', {
      nextPageToken: undefined,
      limit: 100,
      cancelToken: undefined,
    });
  }, [isValidConnected, onSearchListsAsync, resourceConfig?.syncOptions.LISTS]);

  return {hasNoListAccess, onSearchListsAsync};
}

/*
 * Helpers.
 */

function checkForCredentialsError(error: any) {
  return isString(error?.body?.message) && error?.body?.message?.includes('Credentials');
}

function checkHasListAccessError(error: any) {
  return isString(error?.body?.message) && error?.body?.message?.includes('contacts-lists-access');
}

function computeListId(resourceConfig: CrmSyncsPageResourceConfig, formState: CrmSyncsFormModel) {
  if (Object.keys(resourceConfig.syncOptions).length === 0) {
    return undefined;
  }
  const listId =
    formState.selectedListOption.type === CrmSyncOptionsEnum.ALL
      ? EXTERNAL_LINK_SYNC_ALL
      : formState.selectedListOption.listId;
  return listId;
}
